import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const PICKER_MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface MonthYearPickerProps {
  value: string; // YYYY-MM
  onChange: (value: string) => void;
  accentColor?: string;
  /**
   * Whether months after the current one can be picked. Off by default,
   * because a leaderboard or a report can only describe a month that has
   * happened. Screens where a manager sets something up in advance — next
   * month's targets, next month's slabs — turn it on.
   */
  allowFuture?: boolean;
  /** How many years back the year selector reaches. */
  yearsBack?: number;
  className?: string;
}

/** Mirrors DateRangePicker's shape (trigger button, popup panel, header with
 *  prev/next + a year selector) but one level up: the header's year selector
 *  is the same idea, and a grid of MONTHS stands in for the day grid, since
 *  the period these screens work in is a month, not a day. */
const MonthYearPicker: React.FC<MonthYearPickerProps> = ({
  value, onChange, accentColor = '#10b981', allowFuture = false,
  yearsBack = 10, className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [yearStr, monthStr] = value.split('-');
  const selectedYear = parseInt(yearStr, 10);
  const selectedMonthIdx = parseInt(monthStr, 10) - 1;
  const [viewYear, setViewYear] = useState(selectedYear);

  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonthIdx = today.getMonth();
  // With future months allowed the selector still has to stop somewhere;
  // a year ahead covers planning without turning into an endless list.
  const maxYear = allowFuture ? currentYear + 1 : currentYear;

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = maxYear; y >= currentYear - yearsBack; y -= 1) years.push(y);
    return years;
  }, [currentYear, maxYear, yearsBack]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOpen = () => {
    setViewYear(selectedYear);
    setIsOpen(true);
  };

  const isDisabledMonth = (idx: number) => {
    if (allowFuture) return viewYear > maxYear;
    return (viewYear === currentYear && idx > currentMonthIdx) || viewYear > currentYear;
  };

  const handleMonthClick = (idx: number) => {
    if (isDisabledMonth(idx)) return;
    onChange(`${viewYear}-${String(idx + 1).padStart(2, '0')}`);
    setIsOpen(false);
  };

  const displayLabel = new Date(selectedYear, selectedMonthIdx, 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
        className="flex items-center gap-2 h-[42px] px-3.5 text-sm border border-slate-200 shadow-sm rounded-xl bg-white text-slate-700 outline-none hover:bg-slate-50 transition-all duration-200 cursor-pointer font-semibold whitespace-nowrap"
      >
        <Calendar size={16} className="text-slate-400 shrink-0" />
        <span>{displayLabel}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-[260px] bg-white rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.12)] border border-slate-100 z-50 p-3 overflow-hidden font-sans">
          {/* Header — prev/next YEAR (not month) either side of a year select */}
          <div className="flex items-center justify-between gap-1 mb-3">
            <button
              type="button"
              onClick={() => setViewYear((y) => Math.max(y - 1, currentYear - yearsBack))}
              disabled={viewYear <= currentYear - yearsBack}
              className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Previous Year"
            >
              <ChevronLeft size={16} />
            </button>
            <select
              value={viewYear}
              onChange={(e) => setViewYear(Number(e.target.value))}
              className="font-bold text-[#2e3230] text-sm tracking-tight bg-transparent outline-none cursor-pointer appearance-none text-center"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setViewYear((y) => Math.min(y + 1, maxYear))}
              disabled={viewYear >= maxYear}
              className="p-1 hover:bg-slate-50 rounded-full transition-colors text-slate-400 shrink-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Next Year"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Months Grid — stands in for DateRangePicker's day grid */}
          <div className="grid grid-cols-3 gap-1.5">
            {PICKER_MONTH_LABELS.map((label, idx) => {
              const isSelected = viewYear === selectedYear && idx === selectedMonthIdx;
              const isDisabled = isDisabledMonth(idx);
              return (
                <button
                  key={label}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleMonthClick(idx)}
                  className={`h-9 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    isSelected
                      ? 'text-white shadow-sm'
                      : isDisabled
                        ? 'text-slate-300 cursor-not-allowed'
                        : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={isSelected ? { backgroundColor: accentColor } : undefined}
                >
                  {label.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
